import { Navigate, Outlet, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/auth';

/**
 * PublicRoute Component
 *
 * A route guard for public-only routes (like login).
 * Redirects authenticated users to home page or returnUrl if provided.
 *
 * ## Features
 * - **Authenticated redirect**: Sends logged-in users to returnUrl or home
 * - **Return URL support**: Respects returnUrl query param for redirect
 * - **Guest access**: Allows unauthenticated users to proceed
 * - **Nested routes**: Supports React Router's Outlet pattern
 *
 * @example
 * Basic usage for auth routes
 * ```tsx
 * <Route element={<PublicRoute />}>
 *   <Route path="/login" element={<LoginPage />} />
 *   <Route path="/register" element={<RegisterPage />} />
 * </Route>
 * ```
 */
export function PublicRoute() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [searchParams] = useSearchParams();

  if (isAuthenticated) {
    // Respect returnUrl if provided, otherwise redirect to home
    const returnUrl = searchParams.get('returnUrl');
    const redirectTo = returnUrl ? decodeURIComponent(returnUrl) : '/';
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}
